import _extends from "@babel/runtime/helpers/extends";
import React, { PureComponent } from 'react';
import pt from 'prop-types';
import { layers } from '@platform-ui/constants';
import { getContainerZoom, getElementZoom } from '@platform-ui/dom-utils';
import { PopoverContext } from './Context';
import { getBoundingClientRect, getViewportRect, isInPopover, onRectChange, setPosition, setTouchzone } from './utils';
import s from './Popover.module.css';
export const noop = () => {};
const ZOOM_ATTR_NAME = 'data-tui-html-zoom';
const propTypes = process.env.NODE_ENV !== "production" ? {
  direction: pt.oneOfType([pt.oneOf(['left', 'top', 'right', 'bottom']), pt.array]),
  align: pt.oneOf(['start', 'center', 'end', 'stretch'])
} : {};
class BasePopover extends PureComponent {
  constructor(...args) {
    super(...args);
    this.setCorrectionBlock = elem => {
      this.correctionBlock = elem;
    };
    this.setPopover = elem => {
      this.popover = elem;
      let offsetWidth = elem == null ? void 0 : elem.offsetWidth;
      let offsetHeight = elem == null ? void 0 : elem.offsetHeight;
      if (elem) {
        this.observer = new MutationObserver(() => {
          if (elem.offsetWidth !== offsetWidth || elem.offsetHeight !== offsetHeight) {
            offsetWidth = elem.offsetWidth;
            offsetHeight = elem.offsetHeight;
            this.setTooltipPosition();
          }
        });
        this.observer.observe(elem, {
          attributes: true,
          childList: true,
          subtree: true
        });
      } else {
        var _this$observer;
        (_this$observer = this.observer) == null || _this$observer.disconnect();
      }
    };
    this.setPopoverContainer = elem => {
      this.popoverContainer = elem;
    };
    /* eslint-disable max-statements, complexity */
    this.setTooltipPosition = () => {
      const targetEl = this.getTargetElement();
      if (!this.getIsActive() || !this.popover || !targetEl || !this.correctionBlock) {
        return;
      }
      const {
        direction,
        align,
        additionalShift,
        onActiveToggle,
        windowMargin,
        customPadding,
        smartDirection,
        minArrowOffset,
        arrowRect,
        arrowAdaptiveBorderRadiusValue,
        zoomParams
      } = this.props;
      const {
        intersectElems,
        zoom: popoverZoom
      } = this.context;
      const zoom = zoomParams || popoverZoom;

      /**
       * Если на html-теге не установлено значение zoom, то на ретине
       * window.getComputedStyle(document.documentElement).zoom вернет 2.
       * https://bugs.chromium.org/p/chromium/issues/detail?id=899707#c12
       * Аналогично, если установлен зум в браузере,
       * то window.getComputedStyle(document.documentElement).zoom вернет
       * значение зума браузера, а значит не получится определить,
       * от браузера пришло значение, или из стилей.
       * Оба кейса решаются, если на html будет установлено значение zoom.
       */
      const initialZoomStyle = document.querySelector(`[${ZOOM_ATTR_NAME}]`);
      if (document.head && !initialZoomStyle) {
        const style = document.createElement('style');
        style.setAttribute(ZOOM_ATTR_NAME, true);
        style.appendChild(document.createTextNode('html { zoom: 1 }'));
        document.head.prepend(style);
      }
      const htmlZoom = getElementZoom(document.documentElement);
      // eslint-disable-next-line compat/compat
      const bodyZoom = getElementZoom(document.body);
      const {
        zoomValue,
        applyZoomToContainer
      } = getContainerZoom(zoom, {
        targetElement: targetEl,
        destElement: this.popoverContainer
      });
      if (zoomValue !== 1) {
        if (applyZoomToContainer) {
          this.popoverContainer.style.zoom = zoomValue;
        } else {
          this.popover.style.zoom = zoomValue;
        }
      }
      const childrenRect = getBoundingClientRect(targetEl);
      const viewportRect = getViewportRect(this.popoverContainer.parentNode, intersectElems);
      const containerRect = getBoundingClientRect(this.popoverContainer);
      const containerOffsets = {
        top: 0,
        left: 0
      };
      if (window && this.popoverContainer === document.body) {
        const correctionBlockRect = this.correctionBlock.getBoundingClientRect();
        // Нам известно что блок должен стоять на 100 пикселей от края
        // В хроме оффсеты и скролл отдаются без учета зума на body / html,
        // то есть будут 100 нужно корректировать
        // В сафари отступы и скролл отдаются с учетом зума на body / html,
        // то есть будут больше 100 не нужно корректировать
        const chromeZoomCorrection = Math.round(correctionBlockRect.left) === 100 && Math.round(correctionBlockRect.top) === 100;
        let containerZoomCorrection = 1;
        let scrollZoomCorrection = chromeZoomCorrection ? bodyZoom * htmlZoom : 1;
        if (!applyZoomToContainer && zoomValue !== 1) {
          // когда контейнер body нужна коррекция его размеров на zoom вложенных блоков
          // иначе они шире / выше размеров контейнера и не правильно работает
          // определение переполнения контейнера и сдвига поповера относительно края
          containerRect.width /= zoomValue;
          containerRect.height /= zoomValue;
          scrollZoomCorrection = chromeZoomCorrection ? bodyZoom * htmlZoom * zoomValue : scrollZoomCorrection;
          containerZoomCorrection = chromeZoomCorrection ? zoomValue : containerZoomCorrection;
        }
        // корректируем значение отступов контейнера на zoom
        containerRect.top /= containerZoomCorrection;
        containerRect.left /= containerZoomCorrection;
        containerRect.right /= containerZoomCorrection;
        containerRect.bottom /= containerZoomCorrection;
        // корректируем смещение на скролл относительно отступов контейнера и учитываем zoom
        containerOffsets.left = containerRect.left + window.scrollX / scrollZoomCorrection;
        containerOffsets.top = containerRect.top + window.scrollY / scrollZoomCorrection;
      }

      // корректируем отступы таргета поповера относительно контейнера
      childrenRect.top -= containerRect.top;
      childrenRect.left -= containerRect.left;
      childrenRect.right -= containerRect.left;
      childrenRect.bottom -= containerRect.top;
      const {
        scrollTop
      } = this.popoverContainer;
      const popoverRect = getBoundingClientRect(this.popover);
      const options = {
        viewportRect,
        popoverRect,
        childrenRect,
        containerRect,
        containerOffsets,
        containerScroll: scrollTop >= 0 ? scrollTop : document.documentElement.scrollTop,
        smartDirection,
        align,
        windowMargin,
        additionalShift,
        customPadding,
        minArrowOffset,
        direction,
        arrowRect,
        arrowAdaptiveBorderRadiusValue
      };
      const [newDirection, {
        tooltipStyle = {},
        cornerStyle = {}
      }] = setPosition(options);
      const touchzone = setTouchzone({
        popoverRect: _extends({}, popoverRect, tooltipStyle),
        containerRect,
        direction
      });
      // стейт используется в Popover и AnchorPopover классах
      /* eslint-disable react/no-unused-state */
      this.setState({
        left: tooltipStyle.left,
        top: tooltipStyle.top,
        width: tooltipStyle.width,
        height: tooltipStyle.height,
        direction: newDirection,
        touchzone,
        cornerTop: cornerStyle == null ? void 0 : cornerStyle.top,
        cornerLeft: cornerStyle == null ? void 0 : cornerStyle.left
      });
      /* eslint-enable react/no-unused-state */

      onActiveToggle(newDirection, cornerStyle, {
        tooltipStyle,
        popoverRect,
        zoom: {
          popover: zoomValue,
          body: bodyZoom,
          html: htmlZoom
        }
      });
    };
    this.offChildrenBlockRectChange = noop;
    this.handleClickOutside = e => {
      let outsideClick;
      if (e.composedPath) {
        const path = e.composedPath();
        outsideClick = path.indexOf(this.getTargetElement()) === -1 && path.indexOf(this.popover) === -1;
      } else {
        outsideClick = !isInPopover(e.target, this.getTargetElement()) && !isInPopover(e.target, this.popover);
      }
      if (this.props.hideOnClickOutside && this.getIsActive() && outsideClick) {
        var _this$props$onClickOu, _this$props;
        this.close();
        (_this$props$onClickOu = (_this$props = this.props).onClickOutside) == null || _this$props$onClickOu.call(_this$props, e);
      }
    };
    this.handleWindowResize = () => {
      if (this.props.hideOnResize) {
        this.close();
      }
    };
    this.handleWindowTouchMove = () => {
      if (this.props.hideOnTouchMove) {
        this.close();
      }
    };
  }
  componentDidMount() {
    if (this.getIsActive()) {
      this.addListeners();
    }
  }
  componentDidUpdate(prevProps, prevState) {
    if (this.getActiveChanged(prevProps, prevState)) {
      if (this.getIsActive()) {
        this.addListeners();
      } else {
        this.removeListeners();
      }
    }
  }
  componentWillUnmount() {
    this.removeListeners();
  }
  /* eslint-enable max-statements, complexity */
  addListeners() {
    window.addEventListener('resize', this.handleWindowResize);
    window.addEventListener('touchmove', this.handleWindowTouchMove);
    window.addEventListener('mousedown', this.handleClickOutside);
    this.offChildrenBlockRectChange();
    this.offChildrenBlockRectChange = onRectChange(() => {
      // console.log('observer getRect', this.getTargetElement(), this.popoverContainer, this.popover);
      if (!this.getTargetElement() || !this.popoverContainer || !this.popover) {
        // console.log('observer getRect return');
        return null;
      }
      const childrenRect = getBoundingClientRect(this.getTargetElement());
      const containerRect = getBoundingClientRect(this.popoverContainer);
      const relativeChildrenRect = {
        top: childrenRect.top - containerRect.top,
        left: childrenRect.left - containerRect.left,
        width: childrenRect.width,
        height: childrenRect.height
      };
      return relativeChildrenRect;
    }, () => {
      this.setTooltipPosition();
    });
  }
  removeListeners() {
    window.removeEventListener('resize', this.handleWindowResize);
    window.removeEventListener('touchmove', this.handleWindowTouchMove);
    window.removeEventListener('mousedown', this.handleClickOutside);
    this.offChildrenBlockRectChange();
    this.offChildrenBlockRectChange = noop;
  }

  /**
   * Служит для определения когда нужно накладывать коррекцию для zoom
   * Мы делаем fixed position на обертку и ставим в левый верхний угол экрана,
   * внутри блок position absolute с top и left 100px,
   * далее смотрим getBoundingClientRect вложенного блока.
   * Таким образом получаем маркер когда нужна коррекция на зум:
   * В Chrome нужна коррекция и отступ всегда будет около 100 с округлением от зума.
   * В Safari не нужна коррекция и значение всегда не равно 100.
   */
  renderCorrectionBlock() {
    return /*#__PURE__*/React.createElement("div", {
      className: s.correctionBlockWrapper
    }, /*#__PURE__*/React.createElement("div", {
      className: s.correctionBlock,
      ref: this.setCorrectionBlock
    }));
  }
}
BasePopover.contextType = PopoverContext;
BasePopover.defaultProps = {
  dataQaType: 'uikit/popover',
  animated: true,
  onActiveToggle: noop,
  direction: 'bottom',
  smartDirection: true,
  hideOnResize: false,
  renderPopoverBlock: noop,
  windowMargin: 18,
  increasedTouchZone: true,
  additionalShift: 0,
  customPadding: 0,
  zIndex: layers['layer-popover'],
  closeOnPopoverClick: true,
  getSetPositionFunc: noop,
  align: 'center',
  minArrowOffset: 0,
  hideOnTouchMove: false,
  hideOnClickOutside: true,
  arrowRect: null,
  arrowAdaptiveBorderRadiusValue: 0
};
process.env.NODE_ENV !== "production" ? BasePopover.propTypes = {
  /**
   * Принудительно отображает содержимое prop popoverContent
   */
  isActive: pt.bool,
  /**
   * направление/массив приоритетов направлений появления всплывашки
   */
  direction: propTypes.direction,
  /**
   * флаг умного определения позиции всплывашки, не дающего вылазить ей за экран
   */
  smartDirection: pt.bool,
  /**
   * Флаг, скрывать ли компонент при ресайзе
   */
  hideOnResize: pt.bool,
  /**
   * контент всплывашки
   */
  popoverBlock: pt.node,
  /**
   * Функция для рендера всплывашки, принимающая this.close в качестве аргумента.
   * Нужна для компонентов, где требуется ручное закрытие Popover.
   */
  renderPopoverBlock: pt.func,
  /**
   * отступ от границ контейнера (ближайший PortalContainer или body)
   */
  windowMargin: pt.number,
  /**
   * Выравнивание всплывашки относительно контента
   */
  align: propTypes.align,
  /**
   * Увеличенная ховер-зона вокруг всплывашки
   */
  increasedTouchZone: pt.bool,
  /**
   * Кастомный сдвиг перпендикулярно текущему направлению всплывашки
   */
  additionalShift: pt.number,
  /**
   * Кастомный отступ от контента
   */
  customPadding: pt.number,
  /**
   * Кастомный z-index (без крайней необходимости не использовать)
   */
  zIndex: pt.oneOfType([pt.string, pt.number]),
  /**
   * Скрывать поповер по клику на него
   */
  closeOnPopoverClick: pt.bool,
  /**
   * ВНИМАНИЕ: нейминг пропа не соответствует фактическому назначению:
   * Обработчик отвечает за изменение позиции самого поповера и уголка.
   * Для обработки открытия/закрытия нужно использовать onClose
   */
  onActiveToggle: pt.func,
  /**
   * Атрибут data-qa-type компонента
   */
  dataQaType: pt.string,
  /**
   * Флаг анимированного появления/скрытия компонента
   */
  animated: pt.bool,
  /**
   * Задает ограничения для позиционирования стрелок относительно краев
   */
  minArrowOffset: pt.number,
  /**
   * Включает скрытие PopoverBlock на мобильных при событии TouchMove
   */
  hideOnTouchMove: pt.bool,
  /**
   * Включает скрытие поповера по внешнему клику
   */
  hideOnClickOutside: pt.bool,
  /**
   *  Принудительно рендерит контент в <body>
   *  @deprecated используйте to='body'
   */
  toBody: pt.bool,
  /**
   * Куда рендерить контент: `body` либо значение `exitId` нужного PortalExit
   */
  to: pt.string,
  /**
   * Обработчик на клик вне Popover'а
   */
  onClickOutside: pt.func,
  /**
   * Инлайн-стили блока с контентом
   */
  // eslint-disable-next-line react/forbid-prop-types
  blockStyles: pt.object,
  /**
   * Тег создаваемый порталом
   */
  portalTagName: pt.string,
  /**
   * Проп позволяет добавить собственный css класс. ⚠️ Используйте только в крайнем случае ⚠️
   */
  UNSAFE_className: pt.string,
  /**
   * Rect уголка, используется для правильного позиционирования
   */
  // eslint-disable-next-line react/forbid-prop-types
  arrowRect: pt.object,
  /**
   * Отключение стилей border-radius'а при краевых позициях уголка
   * @default 0
   */
  arrowAdaptiveBorderRadiusValue: pt.number
} : void 0;
export default BasePopover;